import { type JanusCtx, type Row, ecId, inventoryId, itemField, record, upper, handBack, definitionNotHeld } from '../ability/context.js';
import { readInventoryField } from '../compile/inventory-raw-readers.js';
import { computedValue, posCoord } from '../../shared/array-types.js';
import { normalizeInventoryGeneration } from '../compile/inventory-generation.js';
import { createVitalityReader, hydrateVitality } from '../compile/vitality-reader.js';
import { normalizeInventoryTransform } from '../compile/inventory-transform.js';
import { roundPhpDecimal } from '../compile/php-decimal-values.js';
import { objectVitalityMax } from '../compile/object-vitality.js';
import { TINY_CREATURE_SIZE_ID } from '../compile/creature-size.js';
import { sizeBandUpdates, vitalityScaleOf } from './transform-edit.js';
import { ftWallRunCarvePieces, ftWallRunPieceCenter, ftWallRunSectionMin } from '../port/k2-geometry/wall_run_carve.js';
import { wallRunSizeIdForLength } from './wall-run-size.js';
import { wallRunPieceDescriptor, type WallRunPiece } from './wall-run-piece.js';
import { json_encode, JSON_UNESCAPED_SLASHES, round } from '../php-compat/index.js';
import { sha256 } from '../port/k2-geometry/sha256.js';

const native = <T>(value: unknown): T => JSON.parse(String(json_encode(value))) as T;

/** The generation birth fingerprint, from the exact row columns the browser asks PHP to store. */
export function wallPieceFingerprint(row: Row): string {
  const transform = normalizeInventoryTransform(row.transform_3d);
  const state: Row = { itemId: upper(row.inventory_item_id), possessId: upper(row.possess_id),
    x: Number(round(Number(row.x_pos ?? 0), 3)), y: Number(round(Number(row.y_pos ?? 0), 3)), z: Number(round(Number(row.z_pos ?? 0), 3)),
    rotationDegrees: roundPhpDecimal(Number(transform?.rotationDegrees ?? 0), 2), scale: roundPhpDecimal(Number(transform?.scale ?? 1), 4),
    meshId: upper(row.mesh_id), size: String(row.inventory_size ?? ''), qty: Number(row.item_qty ?? 0), name: String(row.inventory_name ?? ''),
    vCurrent: Number(row.v_current ?? 0), vMax: Number(row.v_max ?? 0), equipped: String(row.equipped ?? ''),
    unidentified: Number(row.inventory_unidentified ?? 0), children: 0 };
  return sha256(String(json_encode(Object.fromEntries(Object.entries(state).sort(([a], [b]) => a.localeCompare(b))), JSON_UNESCAPED_SLASHES)));
}

/** ft_wall_run_action_key: plans belong to the caster and cast item that selected those sections. */
export function wallRunActionKey(caster: Row, abilityId: string): string {
  return `${ecId(caster) || upper(itemField(caster, 'item_id'))}|${upper(abilityId)}`;
}

/** ft_wall_run_damage_redirect: called only after positive damage, reusing that roll on every live section. */
export function wallRunDamageRedirect(ctx: JanusCtx, caster: Row, target: Row, abilityId: string, area = false): Row[] | null {
  const id = inventoryId(target);
  const carved = ctx.wallRunCarved?.get(id);
  const roster = Array.isArray(ctx.encounter.encounter_inventories_arr) ? ctx.encounter.encounter_inventories_arr as Row[] : [];
  const starts = ctx.wallRunAoeAction === wallRunActionKey(caster, abilityId) ? ctx.wallRunAoeSections?.get(id) : undefined;
  const reached = (row: Row): boolean => {
    if (!area || !ctx.wallRunAoeAllSections?.has(id)) return true;
    const run = record(normalizeInventoryGeneration(ctx.read.inventoryField?.(row, 'inventory_generation')
      ?? readInventoryField(row, 'inventory_generation'))?.wallRun);
    const span = record(run?.span);
    return !!span && !!starts?.some(start => start >= Number(span.min) - 1e-6 && start < Number(span.max) - 1e-6);
  };
  if (carved) return carved.filter(row => roster.some(live => inventoryId(live) === inventoryId(row)) && reached(row));
  const field = (key: string): unknown => ctx.read.inventoryField ? ctx.read.inventoryField(target, key) : readInventoryField(target, key);
  const generation = normalizeInventoryGeneration(field('inventory_generation'));
  const run = record(generation?.wallRun), span = record(run?.span);
  const current = computedValue(target, 'vitality', 'v_current');
  if (itemField(target, 'item_type') !== 'wall' || !generation || !run || !span || Number(span.max) - Number(span.min) <= 5 + 1e-6
      || (current != null && Number(current) <= 0)) return null;
  if (!ctx.saveObjectDamage) handBack('wall-run-damage:section-save-not-supported');
  const templateId = upper(field('inventory_item_id')), template = ctx.read.definition(templateId);
  if (!template) definitionNotHeld('inventory-damage:definition-not-held', templateId);
  const templateVitality = record(createVitalityReader({ resolveInventoryInstance: () => null })(template!));
  const heldMax = Number(templateVitality?.base_v_max ?? templateVitality?.v_max ?? 0);
  const heldSize = upper(ctx.read.value(template!, 'size')) || null;
  const templateSize = heldMax > 0 ? heldSize : heldSize ?? TINY_CREATURE_SIZE_ID;
  const templateMax = heldMax > 0 ? heldMax : objectVitalityMax(template!, { layerValue: ctx.read.value },
    size => vitalityScaleOf(upper(size) || TINY_CREATURE_SIZE_ID), id => {
      const composition = ctx.read.definition(id);
      if (!composition) definitionNotHeld('wall-run-damage:composition-definition-not-held', id);
      return composition;
    }) ?? Number(templateVitality?.base_v_max ?? templateVitality?.v_max ?? ctx.read.value(template!, 'v_max') ?? 0);
  if (area && starts === undefined) handBack('wall-run-damage:area-sections-not-planned');
  let planned = starts;
  if (!planned?.length) {
    const x = posCoord(caster, 'x_pos'), y = posCoord(caster, 'y_pos');
    const along = x != null && y != null && itemField(caster, 'item_type') !== 'encounter'
      ? run.axis === 'x' ? y : x : (Number(span.min) + Number(span.max)) / 2;
    planned = [Number(ftWallRunSectionMin(run, along))];
  }
  const pieces = native<Array<{ min: number; max: number; section: boolean }>>(ftWallRunCarvePieces(run,
    area ? ctx.wallRunAoeAllSections?.get(id) ?? planned : planned));
  if (ctx.inventoryChanged.length + pieces.length + 1 > 128) handBack('wall-run-damage:too-many-sections');
  const rows: Row[] = [], sections: Row[] = [];
  for (const piece of pieces) {
    const center = native<{ x: number; y: number }>(ftWallRunPieceCenter(run, piece.min, piece.max));
    const descriptor = wallRunPieceDescriptor(run as unknown as WallRunPiece, piece.min, piece.max);
    const size = wallRunSizeIdForLength(piece.max - piece.min);
    const health = sizeBandUpdates({ vMax: 0, vCurrent: 0 }, size,
      { sizeId: templateSize, vMax: templateMax });
    const inventory: Row = { inventory_id: ctx.read.uuid(), inventory_item_id: templateId, possess_id: upper(field('possess_id')),
      item_qty: 1, inventory_name: String(field('inventory_name') ?? ''), inventory_size: size,
      x_pos: center.x, y_pos: center.y, z_pos: Number(field('z_pos') ?? posCoord(target, 'z_pos') ?? 0),
      v_max: health.vMax ?? Number(templateVitality?.v_max ?? 5),
      v_current: health.vCurrent ?? Number(templateVitality?.v_current ?? templateVitality?.v_max ?? 5), v_temp: 0, equipped: '' };
    for (const key of ['transform_3d', 'mesh_id', 'inventory_presentation_pic_id', 'inventory_thumbnail_pic_id']) {
      const value = field(key); if (value != null && value !== '') inventory[key] = value;
    }
    inventory.inventory_generation = { ...generation, placementId: `wall-${descriptor.key}`, requestId: `wall-run:${descriptor.key}`,
      wallRun: descriptor, carvedFrom: String(generation.carvedFrom ?? generation.placementId), fingerprint: wallPieceFingerprint(inventory) };
    // add_inventory starts distinct template instances: parent statuses, links and contents do not transfer.
    const row: Row = { ...template!, inventory, position: { x_pos: inventory.x_pos, y_pos: inventory.y_pos, z_pos: inventory.z_pos },
      catalog: target.catalog, permissions: target.permissions, statuses: { target: [] },
      computed: { ...record(template!.computed), possessor: { type: 'encounter' },
        vitality: hydrateVitality({ base_v_max: inventory.v_max, base_v_current: inventory.v_current }) } };
    rows.push(row); if (piece.section) sections.push(row);
  }
  ctx.inventoryChanged.push({ inventory_id: id, changes: {}, carve: { rows: rows.map(row => structuredClone({
    inventory: row.inventory, position: row.position, items: row.items })) } });
  ctx.wallRunCarved ??= new Map(); ctx.wallRunCarved.set(id, sections);
  ctx.encounter.encounter_inventories_arr = [...roster.filter(row => inventoryId(row) !== id), ...rows];
  return sections.filter(reached);
}
